import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import axios from 'axios'
import { Tab } from '@headlessui/react'
import classNames from 'classnames'
import { capitalize } from 'lodash'
import { get, uniqBy } from 'lodash'
import { Plus } from 'lucide-react'
import LoadingSpinner from '../leevo_ui/LoadingSpinner/LoadingSpinner'
import ApplicationLayout from '../leevo_ui/ApplicationLayout/ApplicationLayout'
import EmptyList from '../leevo_ui/EmptyList/EmptyList'
import { Button } from '../ui/button'
import ConfirmationDialog from '../leevo_ui/ConfirmationDialog/ConfirmationDialog'
import toast from '../../utilities/toast'
import ClassesTable from './ClassesTable/ClassesTable'
import ClassEditDrawer from './ClassEditDrawer/ClassEditDrawer'
import SelectInput from '../leevo_ui/SelectInput/SelectInput'
import { requestCreator } from '../../utilities/requests'
import emptyCalendar from '../../images/empty-calendar.svg'
import useFacility from '../../utilities/hooks/useFacility'
import moment from 'moment'

const { get: getKlasses, cancel: cancelKlassesRequest } = requestCreator()
const { delete: deleteKlass } = requestCreator()

function FacilitiesKlassesIndex() {
  const { facility, isLoading: isFacilityLoading } = useFacility()
  const history = useHistory()
  const location = useLocation()
  const [klasses, setKlasses] = useState({
    active: [],
    past: []
  })
  const [selectedKlassPackage, setSelectedKlassPackage] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [editingKlassId, setEditingKlassId] = useState(null)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [klassToDelete, setKlassToDelete] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const klassPackageOptions = useMemo(() => {
    function optionsForState(state) {
      const options = klasses[state]
        .map(({ klassPackages }) => klassPackages)
        .flat()
        .filter((x) => !!x)
        .map(({ title: label, id: value }) => ({ label, value }))
      options.unshift({ label: 'All', value: '' })
      return uniqBy(options, 'value')
    }

    if (klasses.length === 0) {
      return null
    } else {
      return {
        active: optionsForState('active'),
        past: optionsForState('past')
      }
    }
  }, [klasses])

  function tabClassNames({ selected }) {
    return classNames(
      'w-full py-2.5 text-sm leading-5 font-medium rounded-lg',
      'focus:outline-none focus:ring-2 ring-offset-2 ring-offset-purple-400 ring-purple-400 ring-opacity-60',
      selected ? 'text-purple-700 bg-white shadow' : 'text-purple-500 hover:bg-white/[0.12] hover:text-purple-700'
    )
  }

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const editId = params.get('edit')
    if (editId) {
      setEditingKlassId(editId)
      setDrawerOpen(true)
    }
  }, [location.search])

  const loadKlasses = useCallback(() => {
    getKlasses(`/api/facilities/${facility.id}/klasses`, {
      params: {
        all: true,
        include: 'coaches,supported_skill_levels,klass_packages',
        fields: {
          klass:
            'dayOfWeek,timeOfDay,startDate,endDate,coaches,supported_skill_levels,maxStudents,studentsCount,klass_packages,firstOccurrenceAt,finalOccurrenceAt,multiDay',
          coach: 'fullName,cloudinaryPhotoPublicId',
          skillLevel: 'name',
          klassPackage: 'title'
        }
      }
    })
      .then((klasses) => {
        setKlasses({
          active: klasses.filter(
            ({ finalOccurrenceAt }) => !finalOccurrenceAt || moment(finalOccurrenceAt).isAfter(moment())
          ),
          past: klasses.filter(
            ({ finalOccurrenceAt }) => finalOccurrenceAt && !moment(finalOccurrenceAt).isAfter(moment())
          )
        })
      })
      .then(() => setIsLoading(false))
      .catch((error) => {
        if (!axios.isCancel(error)) {
          if (get(error, 'response.status') === 401) {
            history.replace('/login')
          } else {
            history.replace('/500')
          }
        }
      })
  }, [facility, history])

  useEffect(() => {
    setIsLoading(true)
    if (!isFacilityLoading) {
      loadKlasses()
    }

    return cancelKlassesRequest
  }, [isFacilityLoading, loadKlasses])

  function handleEditClick(klassId) {
    setEditingKlassId(klassId)
    setDrawerOpen(true)
    history.push(`/facilities/${facility.id}/classes?edit=${klassId}`)
  }

  function handleDrawerClose(open) {
    setDrawerOpen(open)
    if (!open) {
      setEditingKlassId(null)
      history.push(`/facilities/${facility.id}/classes`)
    }
  }

  function handleSuccess(message) {
    toast.success(message)
    loadKlasses()
  }

  function handleDeleteClick(klass) {
    setKlassToDelete(klass)
  }

  async function handleDeleteConfirm() {
    setIsDeleting(true)
    try {
      await deleteKlass(`/api/facilities/${facility.id}/klasses/${klassToDelete.id}`)
      toast.success('Class deleted successfully!')
      setKlassToDelete(null)
      loadKlasses()
    } catch (error) {
      const apiError = get(error, 'response.data.error')
      const apiErrors = get(error, 'response.data.errors')
      const detailedError =
        apiError ||
        (apiErrors && Object.values(apiErrors).flat().join(', ')) ||
        'Failed to delete class. Please try again.'
      toast.error(detailedError)
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <ApplicationLayout>
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-6 pt-6">
          <h1 className="text-2xl font-semibold text-gray-900">Classes</h1>
          <Button
            onClick={() => history.push(`/facilities/${facility.id}/classes/new`)}
            className="bg-purple-600 hover:bg-purple-700"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Class
          </Button>
        </div>
      </div>
      {isLoading ? (
        <LoadingSpinner size="1/5" className="flex items-center justify-center h-full" />
      ) : (
        <div className="flex flex-col flex-1 bg-gray-100" data-cy="klass-list">
          <div className="w-full max-w-7xl mx-auto px-4 py-4">
            <Tab.Group manual onChange={() => setSelectedKlassPackage('')}>
              <Tab.List className="flex p-1 mb-3 space-x-1 shadow-inner bg-gray-200/50 rounded-xl w-60">
                {Object.keys(klasses).map((state) => (
                  <Tab key={state} className={tabClassNames}>
                    {capitalize(state)}
                  </Tab>
                ))}
              </Tab.List>

              <Tab.Panels>
                {Object.keys(klasses).map((state) => {
                  let emptyStateMessage
                  if (state === 'active') {
                    emptyStateMessage = 'You have no classes scheduled.'
                  } else {
                    emptyStateMessage = 'You have no past classes.'
                  }

                  const filteredKlasses = klasses[state]
                    .filter(({ klassPackages }) => {
                      return !selectedKlassPackage || klassPackages.map(({ id }) => id).includes(selectedKlassPackage)
                    })
                    .sort((a, b) => new Date(a.firstOccurrenceAt) - new Date(b.firstOccurrenceAt))

                  return (
                    <Tab.Panel key={state}>
                      <div className="bg-white rounded-lg shadow">
                        {klassPackageOptions[state] && klassPackageOptions[state].length > 1 && (
                          <div className="flex flex-col items-start px-4 pt-4 pb-3 space-y-2 border-b border-gray-200 md:flex-row md:items-center md:space-y-0 md:space-x-4">
                            <label className="mr-2 text-sm font-medium text-gray-700 whitespace-nowrap">
                              Filter by class package:
                            </label>
                            <div className="w-full md:w-1/2">
                              <SelectInput
                                options={klassPackageOptions[state]}
                                value={selectedKlassPackage}
                                onChange={(e) => setSelectedKlassPackage(e.target.value)}
                                id="select-klass-package"
                                data-cy="klass-package-select"
                              />
                            </div>
                          </div>
                        )}

                        {klasses[state].length === 0 ? (
                          <div className="flex items-center justify-center py-12">
                            <EmptyList
                              message={emptyStateMessage}
                              svgSrc={emptyCalendar}
                              buttonText="Create One +"
                              onButtonClick={() => history.push(`/facilities/${facility.id}/classes/new`)}
                            />
                          </div>
                        ) : (
                          <ClassesTable
                            klasses={filteredKlasses}
                            onEditClick={handleEditClick}
                            onDeleteClick={handleDeleteClick}
                          />
                        )}
                      </div>
                    </Tab.Panel>
                  )
                })}
              </Tab.Panels>
            </Tab.Group>
          </div>
        </div>
      )}

      <ClassEditDrawer
        open={drawerOpen}
        onOpenChange={handleDrawerClose}
        klassId={editingKlassId}
        facility={facility}
        onSuccess={handleSuccess}
      />

      <ConfirmationDialog
        isOpen={!!klassToDelete}
        onClose={() => setKlassToDelete(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete Class?"
        message={
          klassToDelete
            ? `Deleting ${klassToDelete.dayOfWeek} - ${klassToDelete.timeOfDay} will remove it from your calendar. Students in this class may need to be placed into another. This action cannot be undone.`
            : ''
        }
        confirmText="Delete Class"
        cancelText="Cancel"
        confirmColor="red"
        isLoading={isDeleting}
      />
    </ApplicationLayout>
  )
}

export default FacilitiesKlassesIndex
